import { useLayerStore } from '@/store/use-layer-store';
import { useEditorStore } from '@/store/use-editor-store';
import { EventType, emitter } from '@/utils/emitter.utils';
import generateID from '@/utils/generate-id';
import { LayerUtils } from '@/utils/shape/layer.util';
import { cloneDeep } from 'lodash-es';
import { uniqueName } from '@/utils/fs/name.utils';
import { ShapeType } from '@/services/shape';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUseInAOD } from '@/utils/watchface-export/validShape';
import { canUse } from '@/utils/can-use.utils';
import { useShapeManage } from './use-shape-manage';
import { setRemovedAOD } from './use-aod-cache';
import { DataSourceFormat } from '@/resources/enum';
import { TextString } from '@/services/shape/attributes/text';

export const useThemeManage = () => {
  const layerStore = useLayerStore();
  const editorStore = useEditorStore();
  const watchStore = useWatchfaceStore();
  const { resetSlotWidgetToDisplay } = useShapeManage();

  // 从theme中复制
  const _resetIftttWhenCloneTheme = (newTheme: StoreThemeItem) => {
    newTheme.ifttts?.forEach((ifttt) => {
      ifttt.id = 'clone_' + ifttt.id;

      // 重置ifttt绑定的shapeid
      const shapeId = ifttt.shapeId;
      // 查找原来的id
      const layer = newTheme.children.find(
        (item) => item.attrs['_cloneFromId'] == shapeId
      );
      if (layer) ifttt.shapeId = layer.id;

      // 重置animation id
      ifttt.animations.forEach((item) => {
        item.id = 'clone_' + item.id;
      });
      ifttt.condition.id = 'clone_' + ifttt.condition.id;
    });
  };

  /**
   * 息屏下时间数据源格式降级：去除秒级格式（8:00:00 → 8:00，08:00:00 → 08:00）
   * 递归处理 Slot/Widget 内部子元素
   */
  const _downgradeTimeFormatForAOD = (layers: StoreLayerItem[]) => {
    /** 将含秒的时间格式降级为不含秒的对应格式 */
    const downgradeFormat = (format: DataSourceFormat | undefined) => {
      if (format === DataSourceFormat['Format_8:00:00']) {
        return DataSourceFormat['Format_8:00'];
      }
      if (format === DataSourceFormat['Format_08:00:00']) {
        return DataSourceFormat['Format_08:00'];
      }
      return format;
    };

    const processLayer = (item: StoreLayerItem) => {
      if (['time', 'dualtime'].includes(item.attrs.source)) {
        item.attrs.sourceFormat = downgradeFormat(item.attrs.sourceFormat);
      }
      if (item.attrs.strings) {
        item.attrs.strings = item.attrs.strings.map((string: TextString) => {
          if (string.isSource && ['time', 'dualtime'].includes(string.value)) {
            string.sourceFormat = downgradeFormat(string.sourceFormat);
          }
          return string;
        });
      }
      // 递归处理子元素（Slot/Widget 内部）
      if (item.children?.length) {
        item.children.forEach(processLayer);
      }
      return item;
    };
    return layers.map(processLayer);
  };

  const addNormalTheme = (copyFrom?: StoreThemeItem) => {
    const themes: StoreThemeItem[] = [];
    // 新的样式名称
    let name = copyFrom ? copyFrom.name : '样式1';
    // 确保名称唯一性
    const existsName = layerStore.themes.map((item) => item.name) ?? [];
    name = uniqueName(existsName, name);

    // 获取新的themeStyles
    const _addThemeStyle = (theme: StoreThemeItem) => {
      const _theme = cloneDeep(theme);
      _theme.id = generateID();
      _theme.name = name;
      // 重置所有layout的id
      _theme.children.forEach((layer) => {
        const isSlot = layer.type == ShapeType.Slot && layer.children?.length;
        const widgetIndexToDisplay = isSlot
          ? layer.children?.findIndex(
              (item) => item.id == layer.attrs.widgetIdToDisplay
            )
          : undefined;
        LayerUtils.regenerateId(layer);

        // 如果是容器，需要设置默认的children
        if (isSlot) {
          const index = widgetIndexToDisplay ?? -1;
          layer.attrs.widgetIdToDisplay =
            layer.children?.[index < 0 ? 0 : index]?.id;
        }
      });
      LayerUtils.resetThemeColorId(_theme);
      // 复制IFTTT
      _resetIftttWhenCloneTheme(_theme);
      themes.push(_theme);
      return _theme;
    };

    // 在负责样式时，去除不可复制（继承）的图层
    // 目前仅处理最外层的图层，深层的图层暂不处理，当做可继承
    const _removeUnclonableLayers = (newTheme: StoreThemeItem) => {
      // 不可复制的图层类型
      const unclonableType = [ShapeType.Rive, ShapeType.ApkFile];
      newTheme.children = newTheme.children.filter(
        (item) => !unclonableType.includes(item.type as ShapeType)
      );
    };

    // copyFrom存在，代表复制样式
    if (copyFrom) {
      // 这里查找从哪个样式复制
      const _copyFromTheme = layerStore.themes.find(
        (theme) => theme.name == copyFrom.name && theme.type == copyFrom.type
      );
      if (_copyFromTheme) {
        const newTheme = _addThemeStyle(_copyFromTheme);
        _removeUnclonableLayers(newTheme);
      }
    } else {
      const _theme = LayerUtils.getEmptyThemeStyle();
      _addThemeStyle(_theme);
    }

    // 最后执行插入操作
    themes.forEach((theme) => {
      layerStore.$state.themes.push(theme);
    });

    editorStore.selectTheme(
      themes.find((item) => item.type == 'normal')?.id ?? ''
    );
    // 新增亮屏时，需要自动新增息屏
    if (layerStore.isMultiColorAOD) {
      themes.forEach((theme) => {
        addAODTheme(theme, false);
      });
    } else {
      emitter.emit(EventType['theme:create']);
    }
  };

  /**
   * 新增一个AOD模式的样式
   * @param normalTheme 所对应的normal模式
   */
  const addAODTheme = (normalTheme: StoreThemeItem, isSingleAOD?: boolean) => {
    // // 先删除已存在的
    // layerStore.themes = layerStore.themes.filter(
    //   (item) => item.type == 'normal' || item.name != normalTheme.name
    // );

    // 判断是否已存在
    const item = layerStore.themes.find(
      (item) => item.type == 'AOD' && item.name == normalTheme.name
    );
    if (item) {
      item.isSingleAOD = isSingleAOD ? true : false;
      item.colorGroupTable = normalTheme.colorGroupTable;
      return;
    }

    // 未存在时
    const AODTheme = cloneDeep(normalTheme);
    if (!AODTheme) return;
    // 去除息屏下不支持的元素，并降级时间格式
    AODTheme.children = _downgradeTimeFormatForAOD(
      AODTheme.children.filter(
        (item) => !!canUseInAOD(watchStore.deviceType, item.type as ShapeType)
      )
    );
    AODTheme.id = generateID();
    AODTheme.type = 'AOD';
    AODTheme.isSingleAOD = isSingleAOD ? true : false;

    // 息屏和亮屏配色一一对应，即id和color保持一致（name可不一致）
    // 所有无需重置配色id
    // // 重置全部配色
    // LayerUtils.resetThemeColorGroupTable(AODTheme);

    // 重置所有layerId
    AODTheme.children.forEach((layer) => LayerUtils.regenerateId(layer));
    // 由于id变化，需要修改widgetIdToDisplay的值
    AODTheme.children.forEach((layer) => {
      if (layer.type == ShapeType.Slot) resetSlotWidgetToDisplay(layer);
    });

    // 重置IFTTT绑定的shapeId（因为layerId已重新生成）
    _resetIftttWhenCloneTheme(AODTheme);
    // 如果设备不支持AOD下的IFTTT动效，直接清空
    if (!canUse('animation-in-AOD').valid) {
      AODTheme.ifttts = [];
    } else if (AODTheme.ifttts?.length) {
      // 过滤掉被移除元素对应的IFTTT
      const childIds = new Set(AODTheme.children.map((c) => c.id));
      AODTheme.ifttts = AODTheme.ifttts.filter((ifttt) =>
        childIds.has(ifttt.shapeId)
      );
    }

    // 在第一次新建的时候 也缓存一份数据
    setRemovedAOD(AODTheme.name, AODTheme);
    layerStore.themes.push(AODTheme);

    emitter.emit(EventType['theme:create']);
  };

  const rename = (themeId: string, newVal: string) => {
    const themeStyle = layerStore.findTheme(themeId);
    const name = themeStyle?.name ?? '';

    layerStore.themes
      .filter((item) => item.name == name)
      .forEach((item) => {
        item.name = newVal;
      });

    emitter.emit(EventType['theme:set-attr']);
  };

  const reuseNormalTheme = (themeId: string) => {
    const themeAOD = layerStore.findTheme(themeId) as StoreThemeItem;
    if (themeAOD.type === 'normal') throw '只有息屏样式下可复用对应亮屏样式';
    const themeNormal = layerStore.anotherTheme(themeAOD) as StoreThemeItem;
    // 去除息屏下不支持的元素，并降级时间格式
    themeAOD.children = _downgradeTimeFormatForAOD(
      cloneDeep(themeNormal.children).filter(
        (item) => !!canUseInAOD(watchStore.deviceType, item.type as ShapeType)
      )
    );
    themeAOD.children.forEach((layer) => {
      LayerUtils.regenerateId(layer);
    });
    themeAOD.children.forEach((layer) => {
      if (layer.type == ShapeType.Slot) resetSlotWidgetToDisplay(layer);
    });

    // 继承亮屏的IFTTT动效并重置shapeId
    themeAOD.ifttts = cloneDeep(themeNormal.ifttts ?? []);
    _resetIftttWhenCloneTheme(themeAOD);
    // 如果设备不支持AOD下的IFTTT动效，直接清空
    if (!canUse('animation-in-AOD').valid) {
      themeAOD.ifttts = [];
    } else if (themeAOD.ifttts?.length) {
      // 过滤掉被移除元素对应的IFTTT
      const childIds = new Set(themeAOD.children.map((c) => c.id));
      themeAOD.ifttts = themeAOD.ifttts.filter((ifttt) =>
        childIds.has(ifttt.shapeId)
      );
    }

    if (themeAOD.colorGroupTable)
      themeAOD.colorGroupTable = cloneDeep(themeAOD.colorGroupTable);
    LayerUtils.resetThemeColorId(themeAOD);
    emitter.emit(EventType['theme:create']);
  };

  /**
   * 删除一个normal类型的主题，同时需要将AOD类型的删除
   * @param themeId
   * @returns
   */
  const deleteNormalTheme = (themeId: string) => {
    const themeStyle = layerStore.findTheme(themeId);
    if (!themeStyle) return;
    layerStore.themes = layerStore.themes.filter(
      (item) => item.name != themeStyle.name
    );
    editorStore.selectTheme();
    emitter.emit(EventType['theme:delete']);
  };

  /**
   * 验证theme名称是否合法
   * @param treeId
   * @param value
   * @returns { String } err 错误信息或者空字符串
   */
  const validName = (themeId: string, newName: string) => {
    const themes = layerStore.themes.filter((item) => item.id !== themeId);
    // 验证是否重名
    if (themes.some((item) => item.name == newName)) {
      return '名称重复，请更换';
    }
    if (!newName.length) return '名称不可为空';
    if (newName.length > 10) return '最多10个字符';
  };

  /**
   * 切换息屏方式
   * @param mode single:单息屏；multi:多配色息屏
   */

  const exchangeAODMode = (mode: 'single' | 'multiColor') => {
    const normalThemes = layerStore.themes.filter(
      (theme) => theme.type == 'normal'
    );
    const firstNormalTheme: StoreThemeItem | undefined = normalThemes[0];
    // 单一息屏
    if (mode == 'single') {
      // 切换到 single 时，标记第一个 normal 对应的 AOD 为单息屏（AOD 仍在 themes 中，仅通过 displayThemesForType 过滤展示）
      if (!firstNormalTheme) return;

      const keepAOD = layerStore.themes.find(
        (item) => item.type == 'AOD' && item.name == firstNormalTheme.name
      );
      if (keepAOD) {
        //单一息屏仅通过 isSingleAOD 控制 UI 显示“默认配色”，数据层保留完整配色与资源
        keepAOD.isSingleAOD = true;
      }

      layerStore.aodDisplayMode = 'single';
      editorStore.selectTheme(undefined, 'AOD');
    }

    if (mode == 'multiColor') {
      // 切换回多配色时，所有 AOD 仍在 themes 中，同步配色表与标记；若某 normal 无对应 AOD 则创建
      normalThemes.forEach((theme) => {
        const existingAOD = layerStore.themes.find(
          (item) => item.type == 'AOD' && item.name == theme.name
        );
        if (existingAOD) {
          const oldColorIds =
            existingAOD.colorGroupTable?.map((c) => c.id) ?? [];
          // 样式1 息屏配色与亮屏样式1 一致，其余与各自亮屏一致
          const sourceColorGroupTable =
            firstNormalTheme && theme.id === firstNormalTheme.id
              ? cloneDeep(firstNormalTheme.colorGroupTable)
              : cloneDeep(theme.colorGroupTable);

          existingAOD.colorGroupTable = sourceColorGroupTable;
          existingAOD.isSingleAOD = false;

          // 为“新增”的配色 id 在 AOD 中补齐资源（单一息屏未截断表时通常无新增，仅防亮屏新增配色）
          const newColorIds = existingAOD.colorGroupTable
            .map((c) => c.id)
            .filter((id) => !oldColorIds.includes(id));
          const baseColorId =
            oldColorIds[0] ?? existingAOD.colorGroupTable[0]?.id;
          if (baseColorId && newColorIds.length) {
            newColorIds.forEach((toId) => {
              LayerUtils.copyColorResource(
                existingAOD.children,
                baseColorId,
                toId
              );
            });
          }
        } else {
          addAODTheme(theme);
        }
      });
      layerStore.aodDisplayMode = 'multiColor';
    }
  };

  const updateThemeColorAttr = (
    theme: StoreThemeItem,
    colorId: string,
    attrs: { color?: string; name?: string }
  ) => {
    const themeColor = theme.colorGroupTable.find((item) => item.id == colorId);
    if (!themeColor || !theme) return;
    const { color, name } = attrs;
    // 修改名称
    if (name) themeColor.name = name;
    // 修改颜色
    if (color) themeColor.color = color;

    // 如果在亮屏下修改颜色，需要同步修改对应的息屏颜色
    const AODTheme = layerStore.anotherTheme(theme);
    if (theme.type == 'normal' && color && AODTheme) {
      updateThemeColorAttr(AODTheme, colorId, { color });
    } else {
      emitter.emit(EventType['theme-color:set-attr']);
    }
  };

  /**
   * 为主题增加theme color
   * @param theme
   */
  const addThemeColor = (
    theme: StoreThemeItem,
    newColor: string,
    copyFromId?: string
  ) => {
    if (theme.type !== 'normal') {
      throw '只有亮屏样式下可增加配色';
    }
    const names = theme.colorGroupTable.map((item) => item.name);

    const themeColor: StoreThemeColorItem = {
      name: uniqueName(names, '配色1'),
      id: generateID(),
      color: newColor
    };

    theme.colorGroupTable.push(themeColor);

    if (copyFromId) {
      LayerUtils.copyColorResource(theme.children, copyFromId, themeColor.id);
    }

    // 确保息屏和亮屏保存一致
    if (theme.type == 'normal') {
      const AODTheme = layerStore.anotherTheme(theme);
      if (AODTheme) {
        AODTheme.colorGroupTable.push({ ...themeColor });
        if (copyFromId) {
          LayerUtils.copyColorResource(
            AODTheme.children,
            copyFromId,
            themeColor.id
          );
        }
      }
    }
    emitter.emit(EventType['theme-color:create']);

    // 选择当前新建的
    editorStore.selectThemeColor(themeColor.id);
  };

  const deleteThemeColor = (
    theme: StoreThemeItem,
    colorId: string,
    index?: number
  ) => {
    const _index = theme.colorGroupTable.findIndex(
      (item) => item.id == colorId
    );
    if (_index > -1) {
      theme.colorGroupTable = theme.colorGroupTable.filter(
        (item) => item.id !== colorId
      );
    } else {
      if (index !== undefined) {
        theme.colorGroupTable.splice(index, 1);
      }
    }

    LayerUtils.deleteResorceIfColorNotExists(theme);

    // 同步删除息屏的配色
    if (theme.type == 'normal') {
      const AODTheme = layerStore.anotherTheme(theme);
      if (AODTheme) deleteThemeColor(AODTheme, colorId, _index);
    } else {
      emitter.emit(EventType['theme-color:delete']);
      editorStore.selectThemeColor();
    }
  };

  return {
    addNormalTheme,
    addAODTheme,
    rename,
    reuseNormalTheme,
    validName,
    deleteNormalTheme,
    exchangeAODMode,
    updateThemeColorAttr,
    addThemeColor,
    deleteThemeColor
  };
};
